import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { getDefaultUnitSystem, getLengthUnit, getWeightUnit } from '../utils/units';
import MultiStopInput from './MultiStopInput';
import DriverSelector from './DriverSelector';
import { geocodeAddress } from '../services/routingEngineService';
import { useEquipmentGate } from './EquipmentGate';   // __EQUIP_COMPAT__
import { pickupWindowProblem, localToday } from '../utils/timeWindow';   // __TIME_WINDOWS__
import { reportJobChanged } from '../services/hubPlanning';   // __JOB_CHANGES__
import { truckRequiredOf, truckRequiredValue, selfUnloadOf, tarpCraneConflict, TRUCK_CHOICES } from '../utils/equipmentCompat';   // __OPEN_DECK__
import CargoPicker from './CargoPicker';   // __CARGO_V2__ category, then type

// __OPEN_DECK__ (2026-10-27) the job's billing_data as an object (it may be stored as a JSON string), and a copy with the
// Truck required set ('' = Any truck: the key is taken off, like the hub's Update Job)
const bdObject = (bd) => {
  let o = bd;
  for (let i = 0; i < 2 && typeof o === 'string'; i += 1) { try { o = JSON.parse(o || '{}'); } catch (e) { o = null; } }
  return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
};
export const withTruckRequired = (bd, v) => {
  const o = { ...bdObject(bd) };
  const k = truckRequiredValue(v);
  if (k) o.truck_required = k; else delete o.truck_required;
  return o;
};

const createEmptyStop = () => ({
  address: '',
  time_window_start: '',
  time_window_end: '',
  notes: ''
});

function EditLoadModal({ load, onClose, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const [gateEquipment, equipmentModal] = useEquipmentGate();
  const beforeStopsRef = React.useRef(null);   // __JOB_CHANGES__ the stops as they were when the form opened

  const unitSystem = getDefaultUnitSystem();
  const lengthUnit = getLengthUnit(unitSystem);
  const weightUnit = getWeightUnit(unitSystem);

  const [pickupStops, setPickupStops] = useState([createEmptyStop()]);
  const [deliveryStops, setDeliveryStops] = useState([createEmptyStop()]);

    const [formData, setFormData] = useState({
      customer_id: load.customer_id || '',
      driver_id: load.driver_id || '',
      truck_id: load.truck_id || '',
      commodity: load.commodity || '',
      weight_kg: load.weight_kg || '',
      length_cm: load.length_cm || '',
      width_cm: load.width_cm || '',
      height_cm: load.height_cm || '',
      booker_name: load.booker_name || '',
      estimated_duration_hours: load.estimated_duration_hours || '',
      scheduled_date: load.scheduled_date || '',
      delivery_date: load.delivery_date || '',
      notes: load.notes || '',
      truck_required: truckRequiredOf(load)   // __OPEN_DECK__ Any truck ('') / Open deck truck / Tarp truck
    });

  const fetchData = useCallback(async () => {
    setFetchingData(true);
    try {
      // Fetch customers
      const { data: customersData } = await supabase
        .from('customers')
        .select('*')
        .order('company_name');
      setCustomers(customersData || []);

      // Fetch trucks
      const { data: trucksData } = await supabase
        .from('trucks')
        .select('*')
        .eq('is_active', true)
        .order('truck_number');
      setTrucks(trucksData || []);

      // Fetch existing stops for this load
      const { data: stopsData, error: stopsError } = await supabase
        .from('load_stops')
        .select('*')
        .eq('load_id', load.id)
        .order('stop_type', { ascending: true })
        .order('stop_sequence', { ascending: true });

      if (stopsError) throw stopsError;
      beforeStopsRef.current = stopsData || [];   // __JOB_CHANGES__

      // Separate pickup and delivery stops
      const existingPickups = (stopsData || [])
        .filter(s => s.stop_type === 'pickup')
        .map(s => ({
          id: s.id,
          address: s.address || '',
          latitude: s.latitude != null ? s.latitude : null,
          longitude: s.longitude != null ? s.longitude : null,
          _origAddress: s.address || '',
          time_window_start: s.time_window_start || '',
          time_window_end: s.time_window_end || '',
          notes: s.notes || ''
        }));

      const existingDeliveries = (stopsData || [])
        .filter(s => s.stop_type === 'delivery')
        .map(s => ({
          id: s.id,
          address: s.address || '',
          latitude: s.latitude != null ? s.latitude : null,
          longitude: s.longitude != null ? s.longitude : null,
          _origAddress: s.address || '',
          time_window_start: s.time_window_start || '',
          time_window_end: s.time_window_end || '',
          notes: s.notes || ''
        }));

      setPickupStops(existingPickups.length > 0 ? existingPickups : [createEmptyStop()]);
      setDeliveryStops(existingDeliveries.length > 0 ? existingDeliveries : [createEmptyStop()]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setFetchingData(false);
    }
  }, [load.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate at least one pickup and one delivery address
      const hasPickup = pickupStops.some(stop => stop.address.trim());
      const hasDelivery = deliveryStops.some(stop => stop.address.trim());
      
      if (!hasPickup) {
        throw new Error('At least one pickup address is required');
      }
      if (!hasDelivery) {
        throw new Error('At least one delivery address is required');
      }
      // __TIME_WINDOWS__ a job dated today or later needs a pickup window on its first pickup (historic exempt)
      const twProblem = pickupWindowProblem(formData.scheduled_date, pickupStops.filter(stop => stop.address.trim()), localToday());
      if (twProblem) {
        throw new Error('Job not saved - ' + twProblem);
      }

      // __OPEN_DECK__ the Truck required as edited: a tarp truck can't be the truck crane (the crane truck is open)
      const truckBefore = truckRequiredOf(load);
      const truckNow = truckRequiredValue(formData.truck_required);
      const truckChanged = truckNow !== truckBefore;
      if (truckChanged) {
        const why = tarpCraneConflict(truckNow, selfUnloadOf(load));
        if (why) throw new Error('Job not saved - ' + why);
      }

      if ((load.driver_id || null) !== (formData.driver_id || null)) {
        const { guardReassignAllowed } = await import('../services/routingEngineService');
        const g = await guardReassignAllowed(load.id);
        if (g.tier === 'blocked') { throw new Error(g.reason); }
        if (g.tier === 'override' && !window.confirm('WARNING: ' + g.reason + '\n\nOverride and reassign anyway?')) { return; }
      }
      // __EQUIP_COMPAT__ a new driver (or a new truck for him) vs the job as edited - before anything is written
      // __OPEN_DECK__ ...and when the Truck required changes on a job that has a driver (an open-deck job on a tarp truck warns)
      if (formData.driver_id && ((load.driver_id || null) !== (formData.driver_id || null) || (load.truck_id || '') !== (formData.truck_id || '') || truckChanged)) {
        const eq = await gateEquipment({
          driverId: formData.driver_id, truckId: formData.truck_id || null, date: formData.scheduled_date || undefined, context: 'edit_load', title: 'Edit job',
          loads: [{ ...load, commodity: formData.commodity, notes: formData.notes, estimated_duration_hours: formData.estimated_duration_hours, weight_kg: formData.weight_kg, length_cm: formData.length_cm, width_cm: formData.width_cm, height_cm: formData.height_cm,
            billing_data: withTruckRequired(load.billing_data, truckNow) }],
        });
        if (!eq.proceed) return;
      }

            // Update load
            const { error: loadError } = await supabase
              .from('loads')
              .update({
                customer_id: formData.customer_id,
                driver_id: formData.driver_id || null,
                truck_id: formData.truck_id || null,
                commodity: formData.commodity,
                weight_kg: parseInt(formData.weight_kg) || null,
                length_cm: parseInt(formData.length_cm) || null,
                width_cm: parseInt(formData.width_cm) || null,
                height_cm: parseInt(formData.height_cm) || null,
                booker_name: formData.booker_name || null,
                estimated_duration_hours: parseFloat(formData.estimated_duration_hours) || null,
                scheduled_date: formData.scheduled_date || null,
                delivery_date: formData.delivery_date || null,
                notes: formData.notes || null
              })
              .eq('id', load.id);

      if (loadError) throw loadError;
      // __OPEN_DECK__ the Truck required onto billing_data, read fresh so its other keys (geo, PO, overrides) are kept,
      // in the form it is stored in (the hub writes a JSON string)
      if (truckChanged) {
        const { data: bdRow, error: bdReadError } = await supabase.from('loads').select('billing_data').eq('id', load.id).single();
        if (bdReadError) throw bdReadError;
        const merged = withTruckRequired(bdRow && bdRow.billing_data, truckNow);
        const stored = bdRow && typeof bdRow.billing_data === 'string' ? JSON.stringify(merged) : merged;
        const { error: bdError } = await supabase.from('loads').update({ billing_data: stored }).eq('id', load.id);
        if (bdError) throw bdError;
      }
      // __BOARD_ACTIVITY__ what this edit changed, on the job's history (a driver change is logged by the route reconcile)
      try {
        const { editedFields, logLoadActivity } = await import('../services/activityLogService');
        const changed = editedFields({ ...load, truck_required: truckBefore }, formData);   // __OPEN_DECK__
        if (changed.length) logLoadActivity(load.id, 'load_edited', 'fields', null, changed.join(', '));
        const stopsNow = [...pickupStops, ...deliveryStops].filter((x) => x.address.trim());
        const moved = stopsNow.filter((x) => (x._origAddress || '').trim() !== x.address.trim()).length;
        if (moved) logLoadActivity(load.id, 'stops_changed', 'stops', null, moved + ' stop' + (moved === 1 ? '' : 's') + ' added or changed');
      } catch (e) { console.warn('activity log skipped:', e); }

      // Delete existing CUSTOMER stops. __DRIVER_STOP_UNBILLED__ a fuel/break stop the
      // driver added is not in this form (it only edits pickups/deliveries) and is job
      // history -- route, dwell, hours -- so it is kept, never deleted by an office edit.
      const { error: deleteError } = await supabase
        .from('load_stops')
        .delete()
        .eq('load_id', load.id)
        .in('stop_type', ['pickup', 'delivery']);

      if (deleteError) throw deleteError;

      // Resolve coordinates for each stop so the board's geofencing + live ETA
      // keep working after an edit: reuse stored coords when the address is
      // unchanged, otherwise geocode the new address (fallback to old coords,
      // else null -- a coord-less stop is simply skipped by geofence/ETA).
      const resolveStopCoords = async (stop) => {
        const addr = stop.address.trim();
        if (stop.latitude != null && stop.longitude != null &&
            (stop._origAddress || '').trim() === addr) {
          return { latitude: stop.latitude, longitude: stop.longitude };
        }
        try {
          const g = await geocodeAddress(addr);
          if (g && g.lat != null && g.lng != null) {
            return { latitude: g.lat, longitude: g.lng };
          }
        } catch (e) { /* keep fallback */ }
        return {
          latitude: stop.latitude != null ? stop.latitude : null,
          longitude: stop.longitude != null ? stop.longitude : null
        };
      };

      // Create new pickup stops
      const pickupStopsToInsert = await Promise.all(pickupStops
        .filter(stop => stop.address.trim())
        .map(async (stop, index) => ({
          load_id: load.id,
          stop_type: 'pickup',
          stop_sequence: index + 1,
          address: stop.address.trim(),
          ...(await resolveStopCoords(stop)),
          time_window_start: stop.time_window_start || null,
          time_window_end: stop.time_window_end || null,
          notes: stop.notes || null
        })));

      if (pickupStopsToInsert.length > 0) {
        const { error: pickupError } = await supabase
          .from('load_stops')
          .insert(pickupStopsToInsert);
        if (pickupError) throw pickupError;
      }

      // Create new delivery stops
      const deliveryStopsToInsert = await Promise.all(deliveryStops
        .filter(stop => stop.address.trim())
        .map(async (stop, index) => ({
          load_id: load.id,
          stop_type: 'delivery',
          stop_sequence: index + 1,
          address: stop.address.trim(),
          ...(await resolveStopCoords(stop)),
          time_window_start: stop.time_window_start || null,
          time_window_end: stop.time_window_end || null,
          notes: stop.notes || null
        })));

      if (deliveryStopsToInsert.length > 0) {
        const { error: deliveryError } = await supabase
          .from('load_stops')
          .insert(deliveryStopsToInsert);
        if (deliveryError) throw deliveryError;
      }

      if ((load.driver_id || null) !== (formData.driver_id || null)) {
        try {
          const { reconcileRoutesAfterReassignment } = await import('../services/routingEngineService');
          await reconcileRoutesAfterReassignment({ loadId: load.id, oldDriverId: load.driver_id || null, newDriverId: formData.driver_id || null, scheduledDate: formData.scheduled_date || load.scheduled_date || null });
        } catch (e) { console.warn('route reconcile failed (non-fatal):', e); }
      } else if (formData.driver_id) {
        // __EDIT_ROUTE_REFRESH__ same driver, but the stops were just re-inserted
        // (new rows, no ETAs) and the date may have moved: bring the route back in line.
        try {
          const { refreshRouteAfterEdit } = await import('../services/routingEngineService');
          await refreshRouteAfterEdit({ loadId: load.id, driverId: formData.driver_id, status: load.status,
            oldDate: load.scheduled_date || null, newDate: formData.scheduled_date || load.scheduled_date || null });
        } catch (e) { console.warn('route refresh failed (non-fatal):', e); }
      }
      // __JOB_CHANGES__ the change line on the job + Trello, and the driver's push (the hub decides what is worth it)
      reportJobChanged(load.id, { ...load, truck_required: truckBefore }, beforeStopsRef.current, 'Edit job').then((r) => { if (r && !r.ok) console.warn('job change not reported:', r.message); });

      onSuccess();
    } catch (err) {
      console.error('Error updating load:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (fetchingData) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ color: 'var(--accent-primary)', fontSize: '1.5rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '2rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ color: 'var(--accent-primary)', margin: 0 }}>Edit Load #{load.load_number}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Customer */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Customer *
            </label>
            <select
              name="customer_id"
              value={formData.customer_id}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--accent-primary)',
                borderRadius: '5px',
                color: 'var(--text-primary)',
                fontSize: '1rem'
              }}
            >
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.company_name}
                </option>
              ))}
            </select>
          </div>

                    {/* Booker Name */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                        Booker Name
                      </label>
                      <input
                        type="text"
                        name="booker_name"
                        value={formData.booker_name}
                        onChange={handleChange}
                        placeholder="Customer contact who booked this load"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          backgroundColor: 'var(--surface-2)',
                          border: '1px solid var(--border-default)',
                          borderRadius: '5px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem'
                        }}
                      />
                      <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        The person from the customer who booked this load (via WhatsApp, email, phone, etc.)
                      </p>
                    </div>

                    {/* Commodity */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                        Cargo type *
                      </label>
                      <CargoPicker
                        value={formData.commodity}
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          backgroundColor: 'var(--surface-2)',
                          border: '1px solid var(--accent-primary)',
                          borderRadius: '5px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    {/* Weight */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Weight ({weightUnit}) *
            </label>
            <input
              type="number"
              name="weight_kg"
              value={formData.weight_kg}
              onChange={handleChange}
              required
              placeholder="e.g., 5000"
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--accent-primary)',
                borderRadius: '5px',
                color: 'var(--text-primary)',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Dimensions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Length ({lengthUnit})
              </label>
              <input
                type="number"
                name="length_cm"
                value={formData.length_cm}
                onChange={handleChange}
                placeholder="cm"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '5px',
                  color: 'var(--text-primary)',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Width ({lengthUnit})
              </label>
              <input
                type="number"
                name="width_cm"
                value={formData.width_cm}
                onChange={handleChange}
                placeholder="cm"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '5px',
                  color: 'var(--text-primary)',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Height ({lengthUnit})
              </label>
              <input
                type="number"
                name="height_cm"
                value={formData.height_cm}
                onChange={handleChange}
                placeholder="cm"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '5px',
                  color: 'var(--text-primary)',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          {/* Multi-Stop Pickup Addresses */}
          <MultiStopInput
            stops={pickupStops}
            onChange={setPickupStops}
            stopType="pickup"
            required={true}
          />

          {/* Multi-Stop Delivery Addresses */}
          <MultiStopInput
            stops={deliveryStops}
            onChange={setDeliveryStops}
            stopType="delivery"
            required={true}
          />

          {/* Scheduling - Pickup Date, Delivery Date, Duration */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Pickup Date
              </label>
              <input
                type="date"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '5px',
                  color: 'var(--text-primary)',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Delivery Date
              </label>
              <input
                type="date"
                name="delivery_date"
                value={formData.delivery_date}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '5px',
                  color: 'var(--text-primary)',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Est. Duration (hrs)
              </label>
              <input
                type="number"
                name="estimated_duration_hours"
                value={formData.estimated_duration_hours}
                onChange={handleChange}
                placeholder="e.g., 2"
                step="0.5"
                min="0.5"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '5px',
                  color: 'var(--text-primary)',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          {/* __OPEN_DECK__ (2026-10-27) Truck required: Any truck / Open deck truck / Tarp truck (billing_data.truck_required,
              the same three choices as the hub's booking form, the dispatch app and the customer portal) */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="editTruckRequired" style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Truck required
            </label>
            <select
              id="editTruckRequired"
              name="truck_required"
              data-truck-required-select
              value={formData.truck_required}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--border-default)',
                borderRadius: '5px',
                color: 'var(--text-primary)',
                fontSize: '1rem'
              }}
            >
              {TRUCK_CHOICES.map(([v, label]) => <option key={v || 'any'} value={v}>{label}</option>)}
            </select>
            {tarpCraneConflict(formData.truck_required, selfUnloadOf(load)) && (
              <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'var(--color-danger)' }}>
                {tarpCraneConflict(formData.truck_required, selfUnloadOf(load))}
              </div>
            )}
          </div>

          {/* Driver Assignment with Compatibility Check */}
          <DriverSelector
            value={formData.driver_id}
            onChange={handleChange}
            loadRequirements={{
              weight_kg: formData.weight_kg,
              length_cm: formData.length_cm,
              width_cm: formData.width_cm,
              height_cm: formData.height_cm,
              commodity: formData.commodity,
              estimated_duration_hours: formData.estimated_duration_hours,
              truck_required: truckRequiredValue(formData.truck_required),   // __OPEN_DECK__ the list warns on a unit that does not meet it
              self_unload: selfUnloadOf(load)
            }}
            scheduledDate={formData.scheduled_date}
          />

          {/* Truck (Optional) */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Assign Truck (Optional)
            </label>
            <select
              name="truck_id"
              value={formData.truck_id}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--border-default)',
                borderRadius: '5px',
                color: 'var(--text-primary)',
                fontSize: '1rem'
              }}
            >
              <option value="">No truck assigned yet</option>
              {trucks.map(truck => (
                <option key={truck.id} value={truck.id}>
                  Truck {truck.truck_number} ({truck.license_plate})
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Any special instructions or notes..."
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--border-default)',
                borderRadius: '5px',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          {error && (
            <div style={{
              backgroundColor: 'var(--color-danger)',
              color: 'var(--text-primary)',
              padding: '0.75rem',
              borderRadius: '5px',
              marginBottom: '1rem'
            }}>
              Error: {error}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#555',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: loading ? 'var(--surface-3)' : 'var(--btn-primary-bg)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
      {equipmentModal}
    </div>
  );
}

export default EditLoadModal;
